import { useState, useCallback, useEffect } from 'react';
import axiosInstance from '../axiosInstance';

const CMS_PAGES = '/cms/pages';

export function usePagesList(params = {}) {
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(CMS_PAGES, {
        params: {
          limit: params.limit ?? 10,
          page: params.page ?? 1,
          lang: params.lang ?? 'en',
          status: params.status,
          pageCategory: params.pageCategory,
          contentType: params.contentType,
          search: params.search,
        },
      });
      setData(res?.data?.pages ?? []);
      setPagination(res?.pagination ?? null);
      return res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load pages';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [
    params.limit,
    params.page,
    params.lang,
    params.status,
    params.pageCategory,
    params.contentType,
    params.search,
  ]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  return { data, pagination, loading, error, refetch: fetchList };
}

export function usePageById(id) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchOne = useCallback(async () => {
    if (!id) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.get(`${CMS_PAGES}/${id}`, {
        params: { lang: 'en' },
      });
      setData(res?.data?.page ?? null);
      return res?.data?.page;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to load page';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchOne();
  }, [fetchOne]);

  return { data, loading, error, refetch: fetchOne };
}

export function useCreatePage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (payload) => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.post(CMS_PAGES, payload);
      return res?.data?.page ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to create page';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

export function useUpdatePage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id, payload) => {
    if (!id) throw new Error('Page id is required');
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axiosInstance.put(`${CMS_PAGES}/${id}`, payload);
      return res?.data?.page ?? res;
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to update page';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}

export function useDeletePage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const execute = useCallback(async (id) => {
    if (!id) throw new Error('Page id is required');
    setLoading(true);
    setError(null);
    try {
      await axiosInstance.delete(`${CMS_PAGES}/${id}`);
    } catch (err) {
      const msg = err.response?.data?.message || 'Failed to delete page';
      setError(msg);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return { execute, loading, error };
}
